import React, {
    createContext,
    useContext,
    useReducer,
    useMemo,
    useEffect,
    useCallback,
} from 'react';
//import MockDeficiencyData from '../../Mockdata/MockDeficiencyData';
import axios from 'axios';
import cookie from 'js-cookie';
import { getTodayFormatted } from '../../utils/dateUtils';
// Import the status conversion functions
import {
    getApiStatusValue,
    getDisplayStatusValue,
    STATE_TRANSITIONS,
    canTransition,
} from './constants';
import { getDisplayLabelForInternal } from '../../../config/statusDisplayResolver';

/**
 * Context for managing deficiency data and UI state throughout the application.
 * Provides centralized state management for all deficiency-related operations.
 */
const DeficiencyContext = createContext();

/**
 * Status Flow for Deficiencies
 * This is the official state machine flow that should be implemented:
 *
 * Submitted → Accepted → Assigned → Work Completed → Internally Signed Off → Completed
 *
 * Rejected can be reached from:
 * - Submitted (when initially reviewing)
 * - Internally Signed Off (if final review fails)
 *
 * Special transitions:
 * - Work Completed → Assigned (when work is rejected by internal PM/super)
 */

/**
 * Action type constants for the reducer
 * These define all possible operations that can be performed on the deficiency state
 */
export const ACTIONS = {
    // Core data actions
    CHANGE_STATUS: 'CHANGE_STATUS',
    ASSIGN_DEFICIENCY: 'ASSIGN_DEFICIENCY',
    SET_DEFERRED_COST: 'SET_DEFERRED_COST',
    SET_DUE_DATE: 'SET_DUE_DATE',

    // UI state actions
    SET_BATCH_MODE: 'SET_BATCH_MODE',
    UPDATE_SELECTIONS: 'UPDATE_SELECTIONS',
    CLEAR_SELECTIONS: 'CLEAR_SELECTIONS',

    // Modal actions
    OPEN_MODAL: 'OPEN_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL',

    // Filter actions
    SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
    SET_SORT_TYPE: 'SET_SORT_TYPE',
    TOGGLE_SHOW_COMPLETED: 'TOGGLE_SHOW_COMPLETED',
    TOGGLE_SHOW_REJECTED: 'TOGGLE_SHOW_REJECTED',
    TOGGLE_INTERNAL_ONLY_FILTER: 'TOGGLE_INTERNAL_ONLY_FILTER',
    SET_STATUS_FILTER: 'SET_STATUS_FILTER',
    TOGGLE_STATUS_FILTER: 'TOGGLE_STATUS_FILTER',
    SET_COMPANY_FILTER: 'SET_COMPANY_FILTER',
    TOGGLE_COMPANY_FILTER: 'TOGGLE_COMPANY_FILTER',
    TOGGLE_LOCATION_FILTER: 'TOGGLE_LOCATION_FILTER',
    CLEAR_FLOORPLAN_FILTER: 'CLEAR_FLOORPLAN_FILTER',
    CLEAR_LOCATION_FILTER: 'CLEAR_LOCATION_FILTER',
    CLEAR_ALL_FILTERS: 'CLEAR_ALL_FILTERS',

    // Sidebar actions
    SET_SELECTED_DEFICIENCY: 'SET_SELECTED_DEFICIENCY',
    CLEAR_SELECTED_DEFICIENCY: 'CLEAR_SELECTED_DEFICIENCY',

    // New action
    UPDATE_DEFICIENCY: 'UPDATE_DEFICIENCY',

    // New API-related actions
    FETCH_DEFICIENCIES_START: 'fetch_deficiencies_start',
    FETCH_DEFICIENCIES_SUCCESS: 'fetch_deficiencies_success',
    FETCH_DEFICIENCIES_ERROR: 'fetch_deficiencies_error',

    // Business-related actions (CHANGED from user-related)
    FETCH_BUSINESSES_START: 'fetch_businesses_start',
    FETCH_BUSINESSES_SUCCESS: 'fetch_businesses_success',
    FETCH_BUSINESSES_ERROR: 'fetch_businesses_error',
    SET_CURRENT_USER: 'SET_CURRENT_USER',

    // User-related actions for audit trail
    FETCH_USERS_START: 'fetch_users_start',
    FETCH_USERS_SUCCESS: 'fetch_users_success',
    FETCH_USERS_ERROR: 'fetch_users_error',

    // Deficiency update actions
    UPDATE_DEFICIENCY_START: 'update_deficiency_start',
    UPDATE_DEFICIENCY_SUCCESS: 'update_deficiency_success',
    UPDATE_DEFICIENCY_ERROR: 'update_deficiency_error',

    // Image update actions
    UPDATE_IMAGES_START: 'update_images_start',
    UPDATE_IMAGES_SUCCESS: 'update_images_success',
    UPDATE_IMAGES_ERROR: 'update_images_error',

    // Add a new action type for image deletion
    DELETE_IMAGE_START: 'delete_image_start',
    DELETE_IMAGE_SUCCESS: 'delete_image_success',
    DELETE_IMAGE_ERROR: 'delete_image_error',

    // Deificency Creation Actions
    CREATE_DEFICIENCY_START: 'create_deficiency_start',
    SAVE_NEW_DEFICIENCY: 'save_new_deficiency', // we need to save the deficiency before we can upload images
    CREATE_DEFICIENCY_SUCCESS: 'create_deficiency_success',
    CREATE_DEFICIENCY_ERROR: 'create_deficiency_error',

    // Initial image upload actions for new deficiencies
    ADD_INITIAL_IMAGES_START: 'add_initial_images_start',
    ADD_INITIAL_IMAGES_SUCCESS: 'add_initial_images_success',
    ADD_INITIAL_IMAGES_ERROR: 'add_initial_images_error',

    // Comment actions
    FETCH_COMMENTS_START: 'fetch_comments_start',
    FETCH_COMMENTS_SUCCESS: 'fetch_comments_success',
    FETCH_COMMENTS_ERROR: 'fetch_comments_error',
    ADD_COMMENT_START: 'add_comment_start',
    ADD_COMMENT_SUCCESS: 'add_comment_success',
    ADD_COMMENT_ERROR: 'add_comment_error',
};

/**
 * Constants for modal types used throughout the application
 */
export const MODAL_TYPES = {
    STATUS: 'status',
    ASSIGNEE: 'assignee',
    COST: 'cost',
    DUE_DATE: 'due_date',
};

/**
 * Default values for configurable options
 */
export const DEFAULT_GROUPING = 'urgency';

/**
 * Define the workflow order of statuses to determine their position
 * This will be used for status regression cleanup
 */
const WORKFLOW_ORDER = {
    Submitted: 0,
    Accepted: 1,
    Assigned: 2,
    'Work Completed': 3,
    'Internally Signed Off': 4,
    Completed: 5,
    Rejected: -1, // Special case that can come from various points
};

/**
 * Initial state for the deficiencies reducer
 * Contains all data and UI state needed for the deficiency management feature
 */
const initialState = {
    // Core data
    deficiencies: [], // Empty array by default, will be filled from API
    isLoading: true, // Add loading state
    error: null, // Add error state
    businesses: [],
    businessesLoading: false,
    businessesError: null,
    users: [], // Add users for audit trail
    usersLoading: false,
    usersError: null,

    // Comments state
    comments: {}, // Object with deficiencyId as key and array of comments as value
    commentsLoading: false,
    commentsError: null,

    // UI state
    isBatchMode: false,
    selectedItems: new Set(),

    // Modal state
    openModal: null,

    // Search and filter state
    searchQuery: '',
    sortType: DEFAULT_GROUPING,
    showCompleted: false,
    showRejected: false,
    internalOnlyFilter: false,
    statusFilters: [], // array of status strings (multi-select)
    companyFilters: [], // array of { companyId, companyName } (multi-select)
    locationFilters: [], // array of { floorplanName, roomName } (multi-select, specific rooms)

    // Sidebar state
    selectedDeficiency: null,

    // Current user information (should be populated from authentication context)
    currentUser: {
        role: null, // Will be 'INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN', 'INTERNAL_TRADE', 'EXTERNAL_OWNER', 'EXTERNAL_CONSULTANT', 'EXTERNAL_SUBCONTRACTOR_PM', or 'EXTERNAL_SUBCONTRACTOR_TRADE'
        userId: null,
    },
};

/**
 * Reducer function for managing deficiency state
 * Handles all state transitions in a predictable manner
 *
 * @param {Object} state - Current state
 * @param {Object} action - Action to perform (type and payload)
 * @returns {Object} New state
 */
function deficiencyReducer(state, action) {
    switch (action.type) {
        // Add new cases for API operations
        case ACTIONS.FETCH_DEFICIENCIES_START:
            return {
                ...state,
                isLoading: true,
                error: null,
            };

        case ACTIONS.FETCH_DEFICIENCIES_SUCCESS:
            return {
                ...state,
                deficiencies: action.payload,
                isLoading: false,
                error: null,
            };

        case ACTIONS.FETCH_DEFICIENCIES_ERROR:
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };

        case ACTIONS.CHANGE_STATUS: {
            /**
             * CHANGE_STATUS Reducer Case
             *
             * Handles status transitions for deficiencies, managing both single and batch updates.
             * Updates status-specific metadata fields and maintains UI state consistency.
             *
             * Status Workflow:
             * Submitted → Accepted → Assigned → Work Completed → Internally Signed Off → Completed
             *
             * Rejected can be reached from:
             * - Submitted (when initially reviewing)
             * - Internally Signed Off (if final review fails)
             *
             * Special transitions:
             * - Work Completed → Assigned (when work is rejected by internal PM/super)
             *
             * For each status transition, specific metadata fields are updated:
             * - Accepted: Sets acceptedOn date
             * - Assigned: Sets assignedOn date
             * - Work Completed: Sets workCompletedOn date
             * - Internally Signed Off: Sets internallySignedOffOn date and internallySignedOffById
             * - Completed: Sets deficiencyCompletedOn and completionSignOffById
             * - Rejected: Sets rejectedOn, rejectedById, and rejectedFromStatus
             *
             * @param {Object} state - Current state
             * @param {Object} action - Action containing:
             *   - ids: Single ID or array of IDs to update
             *   - newStatus: Target status for the transition
             * @returns {Object} Updated state with new statuses and metadata
             */
            const { ids, newStatus } = action.payload;

            return {
                ...state,
                deficiencies: state.deficiencies.map((deficiency) => {
                    // Check if this deficiency's status should be updated
                    let shouldUpdate = false;

                    // Handle both single and batch updates
                    if (Array.isArray(ids)) {
                        shouldUpdate = ids.includes(deficiency.deficiencyId);
                    } else {
                        shouldUpdate = deficiency.deficiencyId === ids;
                    }

                    if (!shouldUpdate) {
                        return deficiency;
                    }

                    // Status metadata is now handled by the backend
                    // Return updated deficiency with new status only
                    return {
                        ...deficiency,
                        status: newStatus,
                    };
                }),
                // Clear selections after batch operations to avoid confusion
                selectedItems: Array.isArray(ids) ? new Set() : state.selectedItems,
                // Close modal after batch operations for better UX
                openModal: Array.isArray(ids) ? null : state.openModal,
                // Update selectedDeficiency if it was one of the updated items
                selectedDeficiency:
                    state.selectedDeficiency &&
                    ((Array.isArray(ids) && ids.includes(state.selectedDeficiency.deficiencyId)) ||
                        state.selectedDeficiency.deficiencyId === ids)
                        ? state.deficiencies.find(
                              (d) => d.deficiencyId === state.selectedDeficiency.deficiencyId
                          )
                        : state.selectedDeficiency,
            };
        }

        case ACTIONS.ASSIGN_DEFICIENCY: {
            /**
             * ASSIGN_DEFICIENCY Reducer Case
             *
             * Handles assignment of deficiencies to businesses, including batch operations.
             * Updates assignment metadata and may update status if necessary.
             *
             * @param {Object} state - Current state
             * @param {Object} action - Action containing:
             *   - id/ids: Single ID or array of IDs to update
             *   - businessId: Business ID of assignee
             *   - assignee: Display name of assignee (optional)
             * @returns {Object} Updated state with new assignments
             */

            // Extract payload for easier access
            const { id, ids, businessId, assignee } = action.payload;

            // Find business object if we have assignee information
            const business =
                businessId && state.businesses
                    ? state.businesses.find((b) => b.businessId === businessId)
                    : null;

            // Get current user ID for metadata
            const currentUserId =
                action.payload.editorId ||
                cookie.get('userId') || // Use current user ID from cookie
                null;

            return {
                ...state,
                deficiencies: state.deficiencies.map((deficiency) => {
                    // Determine if this deficiency should be updated
                    let shouldUpdate = false;

                    // Handle both single and batch updates
                    if (Array.isArray(ids)) {
                        shouldUpdate = ids.includes(deficiency.deficiencyId);
                    } else {
                        shouldUpdate = deficiency.deficiencyId === id;
                    }

                    if (!shouldUpdate) {
                        return deficiency;
                    }

                    // Get assignment metadata using the centralized helper function
                    const assignmentMetadata = getAssignmentMetadata(
                        businessId,
                        business,
                        deficiency.status,
                        currentUserId
                    );

                    // Add assignee display name if provided explicitly
                    if (assignee) {
                        assignmentMetadata.assignedToBusinessName = assignee;
                    }

                    // Return updated deficiency with assignment metadata
                    return {
                        ...deficiency,
                        ...assignmentMetadata,
                    };
                }),
                // Clear selections after batch operations
                selectedItems: Array.isArray(ids) ? new Set() : state.selectedItems,
                // Close modal after batch operations
                openModal: Array.isArray(ids) ? null : state.openModal,
                // Update selectedDeficiency if it was one of the updated items
                selectedDeficiency:
                    state.selectedDeficiency &&
                    ((Array.isArray(ids) && ids.includes(state.selectedDeficiency.deficiencyId)) ||
                        state.selectedDeficiency.deficiencyId === id)
                        ? state.deficiencies.find(
                              (d) => d.deficiencyId === state.selectedDeficiency.deficiencyId
                          )
                        : state.selectedDeficiency,
            };
        }

        case ACTIONS.SET_DEFERRED_COST:
            return {
                ...state,
                deficiencies: state.deficiencies.map((deficiency) => {
                    // Handle batch updates
                    if (
                        Array.isArray(action.payload.ids) &&
                        action.payload.ids.includes(deficiency.deficiencyId)
                    ) {
                        return { ...deficiency, deferredCost: action.payload.cost };
                    }
                    return deficiency;
                }),
                // Clear selections after batch operations
                selectedItems: new Set(),
                // Close modal after batch operations
                openModal: null,
                // Update selectedDeficiency if it was one of the updated items
                selectedDeficiency:
                    state.selectedDeficiency &&
                    Array.isArray(action.payload.ids) &&
                    action.payload.ids.includes(state.selectedDeficiency.deficiencyId)
                        ? { ...state.selectedDeficiency, deferredCost: action.payload.cost }
                        : state.selectedDeficiency,
            };

        case ACTIONS.SET_DUE_DATE:
            return {
                ...state,
                deficiencies: state.deficiencies.map((deficiency) => {
                    if (
                        Array.isArray(action.payload.ids) &&
                        action.payload.ids.includes(deficiency.deficiencyId)
                    ) {
                        return { ...deficiency, dueDate: action.payload.dueDate };
                    }
                    return deficiency;
                }),
                selectedItems: new Set(),
                openModal: null,
                selectedDeficiency:
                    state.selectedDeficiency &&
                    Array.isArray(action.payload.ids) &&
                    action.payload.ids.includes(state.selectedDeficiency.deficiencyId)
                        ? { ...state.selectedDeficiency, dueDate: action.payload.dueDate }
                        : state.selectedDeficiency,
            };

        // UI state actions
        case ACTIONS.SET_BATCH_MODE:
            return {
                ...state,
                isBatchMode: action.payload,
                // Clear selections if turning off batch mode
                selectedItems: action.payload ? state.selectedItems : new Set(),
            };

        case ACTIONS.UPDATE_SELECTIONS:
            return {
                ...state,
                selectedItems: action.payload,
            };

        case ACTIONS.CLEAR_SELECTIONS:
            return {
                ...state,
                selectedItems: new Set(),
            };

        // Modal actions
        case ACTIONS.OPEN_MODAL:
            return {
                ...state,
                openModal: action.payload,
            };

        case ACTIONS.CLOSE_MODAL:
            return {
                ...state,
                openModal: null,
            };

        // Filter and grouping actions
        case ACTIONS.SET_SEARCH_QUERY:
            return {
                ...state,
                searchQuery: action.payload,
            };

        case ACTIONS.SET_SORT_TYPE:
            return {
                ...state,
                sortType: action.payload,
            };

        case ACTIONS.TOGGLE_SHOW_COMPLETED:
            return {
                ...state,
                showCompleted: !state.showCompleted,
            };

        case ACTIONS.TOGGLE_SHOW_REJECTED:
            return {
                ...state,
                showRejected: !state.showRejected,
            };

        case ACTIONS.TOGGLE_INTERNAL_ONLY_FILTER:
            return {
                ...state,
                internalOnlyFilter: !state.internalOnlyFilter,
            };

        // Sidebar actions
        case ACTIONS.SET_SELECTED_DEFICIENCY:
            return {
                ...state,
                selectedDeficiency: action.payload,
            };

        case ACTIONS.CLEAR_SELECTED_DEFICIENCY:
            return {
                ...state,
                selectedDeficiency: null,
            };

        // New action
        case ACTIONS.UPDATE_DEFICIENCY: {
            const updatedDeficiency = action.payload;

            // Update the deficiency in the main array using deficiencyId instead of id
            const updatedDeficiencies = state.deficiencies.map((deficiency) =>
                deficiency.deficiencyId === updatedDeficiency.deficiencyId
                    ? updatedDeficiency
                    : deficiency
            );

            return {
                ...state,
                deficiencies: updatedDeficiencies,
            };
        }

        // Update deficiency data cases
        case ACTIONS.UPDATE_DEFICIENCY_START:
            return {
                ...state,
            };

        case ACTIONS.UPDATE_DEFICIENCY_SUCCESS: {
            const updatedDeficiency = action.payload;

            // Update the deficiency in the main array using deficiencyId
            const updatedDeficiencies = state.deficiencies.map((deficiency) =>
                deficiency.deficiencyId === updatedDeficiency.deficiencyId
                    ? updatedDeficiency
                    : deficiency
            );

            return {
                ...state,
                deficiencies: updatedDeficiencies,
                // Update selected deficiency if it's the one we just updated
                selectedDeficiency:
                    state.selectedDeficiency?.deficiencyId === updatedDeficiency.deficiencyId
                        ? updatedDeficiency
                        : state.selectedDeficiency,
            };
        }

        case ACTIONS.UPDATE_DEFICIENCY_ERROR:
            return {
                ...state,
                error: action.payload,
            };

        // Image update cases
        case ACTIONS.UPDATE_IMAGES_START:
            return {
                ...state,
            };

        case ACTIONS.UPDATE_IMAGES_SUCCESS: {
            const updatedDeficiency = action.payload;
            const deficiencyId = updatedDeficiency.deficiencyId;

            // Ensure status is correctly mapped for display
            if (updatedDeficiency.status) {
                updatedDeficiency.status = getDisplayStatusValue(updatedDeficiency.status);
            }

            // Update the deficiency in the main array using deficiencyId
            const updatedDeficiencies = state.deficiencies.map((deficiency) => {
                if (deficiency.deficiencyId === deficiencyId) {
                    // Merge the updated data into the existing deficiency
                    return { ...deficiency, ...updatedDeficiency };
                }
                return deficiency;
            });

            // Also update the selected deficiency if it's the one we just updated
            let newSelectedDeficiency = state.selectedDeficiency;
            if (state.selectedDeficiency?.deficiencyId === deficiencyId) {
                // Always merge the new data into selectedDeficiency
                newSelectedDeficiency = {
                    ...state.selectedDeficiency,
                    ...updatedDeficiency,
                };
            }

            return {
                ...state,
                deficiencies: updatedDeficiencies,
                selectedDeficiency: newSelectedDeficiency,
            };
        }

        case ACTIONS.UPDATE_IMAGES_ERROR:
            return {
                ...state,
                error: action.payload,
            };

        // Image delete cases
        case ACTIONS.DELETE_IMAGE_START:
            return {
                ...state,
            };

        case ACTIONS.DELETE_IMAGE_SUCCESS: {
            const { deficiencyId, imageId, imageType } = action.payload;

            // Update the deficiency in the main array
            const updatedDeficiencies = state.deficiencies.map((deficiency) => {
                if (deficiency.deficiencyId === deficiencyId) {
                    // Create a new deficiency object with the modified image array
                    const newDeficiency = { ...deficiency };

                    if (imageType === 'before' && newDeficiency.beforeImages) {
                        newDeficiency.beforeImages = newDeficiency.beforeImages.filter(
                            (img) => img.deficiencyImageId !== imageId
                        );
                    } else if (imageType === 'after' && newDeficiency.afterImages) {
                        newDeficiency.afterImages = newDeficiency.afterImages.filter(
                            (img) => img.deficiencyImageId !== imageId
                        );
                    }

                    return newDeficiency;
                }
                return deficiency;
            });

            // Update the selected deficiency if it's the one we just modified
            let updatedSelectedDeficiency = state.selectedDeficiency;
            if (state.selectedDeficiency?.deficiencyId === deficiencyId) {
                updatedSelectedDeficiency = { ...state.selectedDeficiency };

                if (imageType === 'before' && updatedSelectedDeficiency.beforeImages) {
                    updatedSelectedDeficiency.beforeImages =
                        updatedSelectedDeficiency.beforeImages.filter(
                            (img) => img.deficiencyImageId !== imageId
                        );
                } else if (imageType === 'after' && updatedSelectedDeficiency.afterImages) {
                    updatedSelectedDeficiency.afterImages =
                        updatedSelectedDeficiency.afterImages.filter(
                            (img) => img.deficiencyImageId !== imageId
                        );
                }
            }

            return {
                ...state,
                deficiencies: updatedDeficiencies,
                selectedDeficiency: updatedSelectedDeficiency,
            };
        }

        case ACTIONS.DELETE_IMAGE_ERROR:
            return {
                ...state,
                error: action.payload,
            };

        // Add new cases for user operations
        case ACTIONS.FETCH_BUSINESSES_START:
            return {
                ...state,
                businessesLoading: true,
                businessesError: null,
            };

        case ACTIONS.FETCH_BUSINESSES_SUCCESS:
            return {
                ...state,
                businesses: action.payload,
                businessesLoading: false,
                businessesError: null,
            };

        case ACTIONS.FETCH_BUSINESSES_ERROR:
            return {
                ...state,
                businessesLoading: false,
                businessesError: action.payload,
            };

        // Add user operations (for audit trail)
        case ACTIONS.FETCH_USERS_START:
            return {
                ...state,
                usersLoading: true,
                usersError: null,
            };

        case ACTIONS.FETCH_USERS_SUCCESS:
            return {
                ...state,
                users: action.payload,
                usersLoading: false,
                usersError: null,
            };

        case ACTIONS.FETCH_USERS_ERROR:
            return {
                ...state,
                usersLoading: false,
                usersError: action.payload,
            };

        // Handle current user setting
        case ACTIONS.SET_CURRENT_USER:
            return {
                ...state,
                currentUser: {
                    ...state.currentUser,
                    ...action.payload,
                },
            };

        case ACTIONS.CREATE_DEFICIENCY_START:
            return {
                ...state,
            };

        case ACTIONS.CREATE_DEFICIENCY_SUCCESS: {
            const updatedDeficiency = action.payload;
            return {
                ...state,
                deficiencies: [...state.deficiencies, updatedDeficiency],
            };
        }

        case ACTIONS.CREATE_DEFICIENCY_ERROR:
            return {
                ...state,
                error: action.payload,
            };

        case ACTIONS.ADD_INITIAL_IMAGES_START:
            return {
                ...state,
            };

        case ACTIONS.ADD_INITIAL_IMAGES_SUCCESS: {
            const updatedDeficiency = action.payload;
            const deficiencyId = updatedDeficiency.deficiencyId;

            // Update the deficiency in the main array
            const updatedDeficiencies = state.deficiencies.map((deficiency) => {
                if (deficiency.deficiencyId === deficiencyId) {
                    // Create a new deficiency object with the updated beforeImages
                    return {
                        ...deficiency,
                        beforeImages: updatedDeficiency.beforeImages,
                    };
                }
                return deficiency;
            });

            return {
                ...state,
                deficiencies: updatedDeficiencies,
            };
        }

        case ACTIONS.ADD_INITIAL_IMAGES_ERROR:
            return {
                ...state,
                error: action.payload,
            };

        // SET_ actions are used by dashboard navigation (single value → single-item array)
        case ACTIONS.SET_STATUS_FILTER:
            return {
                ...state,
                statusFilters: [action.payload],
            };

        // TOGGLE_ actions are used by the filter menu (add/remove from array)
        case ACTIONS.TOGGLE_STATUS_FILTER: {
            const status = action.payload;
            const exists = state.statusFilters.includes(status);
            return {
                ...state,
                statusFilters: exists
                    ? state.statusFilters.filter((s) => s !== status)
                    : [...state.statusFilters, status],
            };
        }

        case ACTIONS.SET_COMPANY_FILTER:
            return {
                ...state,
                companyFilters: [action.payload],
            };

        case ACTIONS.TOGGLE_COMPANY_FILTER: {
            const company = action.payload;
            const companyExists = state.companyFilters.some(
                (c) => c.companyId === company.companyId
            );
            return {
                ...state,
                companyFilters: companyExists
                    ? state.companyFilters.filter((c) => c.companyId !== company.companyId)
                    : [...state.companyFilters, company],
            };
        }

        case ACTIONS.TOGGLE_LOCATION_FILTER: {
            const loc = action.payload; // { floorplanName, roomName }

            // Check if the whole floorplan is currently selected (floorplan-level entry)
            const floorplanSelected = state.locationFilters.some(
                (l) => l.floorplanName === loc.floorplanName && l.roomName === null
            );

            // Check if ANY entries exist for this floorplan
            const hasAnyForFloorplan = state.locationFilters.some(
                (l) => l.floorplanName === loc.floorplanName
            );

            // Case 1: Toggling the whole floorplan (roomName === null)
            if (loc.roomName === null) {
                if (hasAnyForFloorplan) {
                    // Any entries exist → clear ALL entries for this floorplan
                    return {
                        ...state,
                        locationFilters: state.locationFilters.filter(
                            (l) => l.floorplanName !== loc.floorplanName
                        ),
                    };
                }
                // No entries → add floorplan-level entry
                return {
                    ...state,
                    locationFilters: [...state.locationFilters, loc],
                };
            }

            // Case 2: Toggling a specific room while the whole floorplan is selected
            // → remove floorplan-level entry, add individual entries for all OTHER rooms
            if (floorplanSelected) {
                const roomsOnFloorplan = new Set();
                state.deficiencies.forEach((d) => {
                    const fp = d.floorplanName || 'Unknown Floor Plan';
                    if (fp === loc.floorplanName) {
                        roomsOnFloorplan.add(d.roiName || 'Unassigned Room');
                    }
                });

                const otherFilters = state.locationFilters.filter(
                    (l) => !(l.floorplanName === loc.floorplanName && l.roomName === null)
                );
                const roomEntries = [...roomsOnFloorplan]
                    .filter((room) => room !== loc.roomName)
                    .map((room) => ({ floorplanName: loc.floorplanName, roomName: room }));

                return {
                    ...state,
                    locationFilters: [...otherFilters, ...roomEntries],
                };
            }

            // Case 3: Normal room toggle (no floorplan-level entry)
            const locExists = state.locationFilters.some(
                (l) => l.floorplanName === loc.floorplanName && l.roomName === loc.roomName
            );
            return {
                ...state,
                locationFilters: locExists
                    ? state.locationFilters.filter(
                          (l) =>
                              !(
                                  l.floorplanName === loc.floorplanName &&
                                  l.roomName === loc.roomName
                              )
                      )
                    : [...state.locationFilters, loc],
            };
        }

        case ACTIONS.CLEAR_FLOORPLAN_FILTER: {
            const floorplanName = action.payload;
            return {
                ...state,
                locationFilters: state.locationFilters.filter(
                    (l) => l.floorplanName !== floorplanName
                ),
            };
        }

        case ACTIONS.CLEAR_LOCATION_FILTER:
            return {
                ...state,
                locationFilters: [],
            };

        case ACTIONS.CLEAR_ALL_FILTERS:
            return {
                ...state,
                statusFilters: [],
                companyFilters: [],
                locationFilters: [],
                showCompleted: false,
                showRejected: false,
                internalOnlyFilter: false,
            };

        // Comment actions
        case ACTIONS.FETCH_COMMENTS_START:
            return {
                ...state,
                commentsLoading: true,
                commentsError: null,
            };

        case ACTIONS.FETCH_COMMENTS_SUCCESS: {
            const { deficiencyId, comments } = action.payload;
            return {
                ...state,
                comments: {
                    ...state.comments,
                    [deficiencyId]: comments,
                },
                commentsLoading: false,
                commentsError: null,
            };
        }

        case ACTIONS.FETCH_COMMENTS_ERROR:
            return {
                ...state,
                commentsLoading: false,
                commentsError: action.payload,
            };

        case ACTIONS.ADD_COMMENT_START:
            return {
                ...state,
                commentsError: null,
            };

        case ACTIONS.ADD_COMMENT_SUCCESS: {
            const { deficiencyId, comment } = action.payload;
            const existingComments = state.comments[deficiencyId] || [];
            return {
                ...state,
                comments: {
                    ...state.comments,
                    [deficiencyId]: [...existingComments, comment],
                },
                commentsError: null,
            };
        }

        case ACTIONS.ADD_COMMENT_ERROR:
            return {
                ...state,
                commentsError: action.payload,
            };

        default:
            return state;
    }
}

/**
 * Generates assignment-specific metadata for assignment changes
 * NOTE: Date fields are handled automatically by the backend
 * NOTE: Status changes from assigning are handled automatically by the backend
 * BUT we still need to set the status field when assigning in early stages
 *
 * @param {string|null} businessId - Business ID being assigned (null for unassignment)
 * @param {Object|null} business - Business object with name (optional)
 * @param {string|null} currentStatus - Current status of the deficiency (needed for auto status change)
 * @param {string} editorId - ID of user making the change (no longer needed but kept for backward compatibility)
 * @returns {Object} Object containing assignment fields only
 */
export function getAssignmentMetadata(businessId, business = null, currentStatus = null, editorId) {
    // Initialize metadata object
    const metadata = {};

    // If businessId is null or empty string, we're unassigning
    if (!businessId || businessId === '') {
        metadata.assignedToBusinessId = null;
        metadata.assignedToBusinessName = null;
        return metadata;
    }

    // We're assigning to a business
    metadata.assignedToBusinessId = businessId;

    // Set display name if business object is provided
    if (business && business.businessName) {
        metadata.assignedToBusinessName = business.businessName;
    }

    // Update status to Assigned if it's in an early stage
    // This is still needed for the frontend to maintain the automatic transition
    if (currentStatus) {
        const earlyStatuses = ['Submitted', 'Accepted'];
        if (earlyStatuses.includes(currentStatus)) {
            metadata.status = 'Assigned';
        }
    }

    return metadata;
}

/**
 * Clears metadata for statuses that come after the new status in the workflow
 * This ensures we don't have orphaned metadata when moving backwards in the workflow
 *
 * @param {string} newStatus - The new status being changed to
 * @param {Object} metadata - The metadata object to clean
 * @returns {Object} Cleaned metadata object
 */
function clearFutureStatusMetadata(newStatus, metadata) {
    // Get position of new status in workflow
    const newStatusPosition = WORKFLOW_ORDER[newStatus];

    // If we can't determine position, don't clear
    if (newStatusPosition === undefined) {
        return metadata;
    }

    // Special handling for Rejected status - don't clear any previous status metadata
    // This preserves the history of what happened before rejection
    if (newStatus === 'Rejected') {
        return metadata;
    }

    // For all other statuses, clear rejection metadata
    metadata.rejectedOn = null;
    metadata.rejectedById = null;
    metadata.rejectedFromStatus = null;

    // Clear metadata for all statuses after the new status position
    if (newStatusPosition < WORKFLOW_ORDER['Accepted']) {
        metadata.acceptedOn = null;
    }

    if (newStatusPosition < WORKFLOW_ORDER['Assigned']) {
        metadata.assignedOn = null;
        metadata.assignedToBusinessName = null;
        metadata.assignedToBusinessId = null;
    }

    if (newStatusPosition < WORKFLOW_ORDER['Work Completed']) {
        metadata.workCompletedOn = null;
    }

    if (newStatusPosition < WORKFLOW_ORDER['Internally Signed Off']) {
        metadata.internallySignedOffOn = null;
        metadata.internallySignedOffById = null;
    }

    if (newStatusPosition < WORKFLOW_ORDER['Completed']) {
        metadata.deficiencyCompletedOn = null;
        metadata.completionSignOffById = null;
    }

    return metadata;
}

/**
 * Utility function to ensure fields are properly preserved during API updates.
 * Handles backward status transitions to clear appropriate metadata.
 * The backend handles setting metadata, but we need to explicitly clear it when moving backward.
 *
 * @param {Object} currentData - The current complete data object
 * @param {Object} updateData - The changes to apply
 * @returns {Object} A sanitized object safe for API updates
 */
function sanitizeForUpdate(currentData, updateData) {
    // Create a merged copy with the updates applied
    const result = { ...currentData, ...updateData };

    // Check if this is a status change operation
    const isStatusChange = updateData.status && updateData.status !== currentData.status;
    const oldStatus = currentData.status;
    const newStatus = updateData.status;

    // Special case for moving FROM Rejected - ensure rejection metadata is cleared
    if (isStatusChange && oldStatus === 'Rejected') {
        // Apply all updates, which will include the nullified rejection fields
        Object.assign(result, updateData);

        // Ensure rejection metadata is explicitly cleared
        result.rejectedOn = null;
        result.rejectedById = null;
        result.rejectedFromStatus = null;

        // Use the existing clearFutureStatusMetadata function to clean up metadata
        // for statuses that come after the new target status
        clearFutureStatusMetadata(newStatus, result);

        // Special handling for assignment fields if they're being explicitly set in this update
        if ('assignedToBusinessId' in updateData) {
            // Restore the explicitly provided assignment values
            result.assignedToBusinessId = updateData.assignedToBusinessId;
            result.assignedToBusinessName = updateData.assignedToBusinessName;
        }

        return result;
    }

    // For backward status changes, we need to clear metadata for all statuses that come after the new status
    // This ensures we don't have orphaned metadata when moving backwards in the workflow
    const isBackwardStatusChange =
        isStatusChange &&
        WORKFLOW_ORDER[newStatus] !== undefined &&
        WORKFLOW_ORDER[oldStatus] !== undefined &&
        WORKFLOW_ORDER[newStatus] < WORKFLOW_ORDER[oldStatus];

    if (isBackwardStatusChange) {
        // Use the clearFutureStatusMetadata function to clean up metadata
        clearFutureStatusMetadata(newStatus, result);

        // Special handling for assignment fields if they're being explicitly set in this update
        if ('assignedToBusinessId' in updateData) {
            // Restore the explicitly provided assignment values
            result.assignedToBusinessId = updateData.assignedToBusinessId;
            result.assignedToBusinessName = updateData.assignedToBusinessName;
        }
    }

    // Preserve dueDate if not explicitly changed (this is a user-editable field)
    if (currentData.dueDate && updateData.dueDate === null) {
        result.dueDate = currentData.dueDate;
    }

    // Handle explicit unassignment - this should be allowed to nullify assignedTo fields
    const isExplicitUnassignment =
        Object.prototype.hasOwnProperty.call(updateData, 'assignedToBusinessId') &&
        (updateData.assignedToBusinessId === null || updateData.assignedToBusinessId === '');

    if (isExplicitUnassignment) {
        result.assignedToBusinessId = null;
        result.assignedToBusinessName = null;
        // Also clear the assignedOn date when explicitly unassigning
        result.assignedOn = null;
    }

    return result;
}

/**
 * Provider component that makes deficiency state available to child components
 * Implements the context pattern for state management
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.projectId - The ID of the project to fetch deficiencies for
 * @param {string} props.floorplanId - Optional ID of the floorplan to fetch deficiencies for (overrides projectId)
 * @returns {React.ReactElement} Provider wrapped around children
 */
export function DeficiencyProvider({ children, projectId, floorplanId }) {
    const [state, dispatch] = useReducer(deficiencyReducer, initialState);

    // Fetch comments for a deficiency - wrapped in useCallback for dependency consistency
    const fetchDeficiencyComments = useCallback(
        async (deficiencyId) => {
            try {
                dispatch({ type: ACTIONS.FETCH_COMMENTS_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.FETCH_COMMENTS_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                const response = await axios.get(
                    `${API_URL}/deficiencies/${deficiencyId}/comments`,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                dispatch({
                    type: ACTIONS.FETCH_COMMENTS_SUCCESS,
                    payload: {
                        deficiencyId,
                        comments: response.data,
                    },
                });
            } catch (error) {
                console.error('Error fetching comments:', error);
                dispatch({
                    type: ACTIONS.FETCH_COMMENTS_ERROR,
                    payload: 'Failed to load comments',
                });
            }
        },
        [dispatch]
    );

    // Add useEffect to fetch deficiencies on component mount
    useEffect(() => {
        const fetchDeficiencies = async () => {
            try {
                // Check if user is authenticated before making API calls
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');
                const userId = cookie.get('userId');

                // Only proceed if we have both authentication tokens
                if (!xsrfToken || !userId) {
                    return;
                }

                dispatch({ type: ACTIONS.FETCH_DEFICIENCIES_START });

                let response;
                // Use floorplan-specific endpoint if floorplanId is provided
                if (floorplanId) {
                    response = await axios.get(
                        `${API_URL}/deficiencies/get-deficiencies-by-floorplan/${projectId}/${floorplanId}`,
                        {
                            withCredentials: true,
                            headers: {
                                'X-XSRF-TOKEN': xsrfToken,
                            },
                        }
                    );
                } else if (projectId) {
                    response = await axios.get(
                        `${API_URL}/deficiencies/get-deficiencies-by-project/${projectId}`,
                        {
                            withCredentials: true,
                            headers: {
                                'X-XSRF-TOKEN': xsrfToken,
                            },
                        }
                    );
                } else {
                    return;
                }

                // Convert API status values to frontend display values
                const deficiencies = response.data.map((deficiency) => {
                    if (deficiency && deficiency.status) {
                        return {
                            ...deficiency,
                            status: getDisplayStatusValue(deficiency.status),
                        };
                    }
                    return deficiency;
                });

                dispatch({
                    type: ACTIONS.FETCH_DEFICIENCIES_SUCCESS,
                    payload: deficiencies,
                });
            } catch (error) {
                let errorMessage = 'Failed to load deficiencies';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to view deficiencies. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.FETCH_DEFICIENCIES_ERROR,
                    payload: errorMessage,
                });
            }
        };

        if (floorplanId || projectId) {
            fetchDeficiencies();
        }
    }, [projectId, floorplanId]);

    // Add new function to load deficiencies for a specific floorplan
    const loadFloorplanDeficiencies = useCallback(async (projectId, floorplanId) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const xsrfToken = cookie.get('XSRF-TOKEN');
            const userId = cookie.get('userId');

            // Only proceed if we have both authentication tokens
            if (!xsrfToken || !userId) {
                return [];
            }

            dispatch({ type: ACTIONS.FETCH_DEFICIENCIES_START });

            const response = await axios.get(
                `${API_URL}/deficiencies/get-deficiencies-by-floorplan/${projectId}/${floorplanId}`,
                {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                }
            );

            // Convert API status values to frontend display values
            const deficiencies = response.data.map((deficiency) => {
                if (deficiency && deficiency.status) {
                    return {
                        ...deficiency,
                        status: getDisplayStatusValue(deficiency.status),
                    };
                }
                return deficiency;
            });

            dispatch({
                type: ACTIONS.FETCH_DEFICIENCIES_SUCCESS,
                payload: deficiencies,
            });

            return deficiencies;
        } catch (error) {
            let errorMessage = 'Failed to load floorplan deficiencies';
            if (error.response?.status === 403) {
                errorMessage = 'Not authorized to view deficiencies. Please log in again.';
            }

            dispatch({
                type: ACTIONS.FETCH_DEFICIENCIES_ERROR,
                payload: errorMessage,
            });

            return [];
        }
    }, []);

    // Add useEffect to fetch businesses on component mount
    useEffect(() => {
        const fetchBusinesses = async () => {
            try {
                // Check if user is authenticated before making API calls
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');
                const userId = cookie.get('userId');

                // Only proceed if we have both authentication tokens
                if (!xsrfToken || !userId) {
                    console.log('User not authenticated - skipping business fetch');
                    return;
                }

                dispatch({ type: ACTIONS.FETCH_BUSINESSES_START });

                const response = await axios.get(
                    `${API_URL}/project/get-businesses-for-project/${projectId}`,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                dispatch({
                    type: ACTIONS.FETCH_BUSINESSES_SUCCESS,
                    payload: response.data,
                });
            } catch (error) {
                console.error('Error fetching businesses:', error);

                let errorMessage = 'Failed to load businesses';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to view businesses. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.FETCH_BUSINESSES_ERROR,
                    payload: errorMessage,
                });

                if (error.response) {
                    console.log('Error response:', error.response.data);
                    console.log('Error status:', error.response.status);
                }
            }
        };

        if (projectId) {
            fetchBusinesses();
        }
    }, [projectId]);

    // Add useEffect to fetch users on component mount (for audit trail)
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                // Check if user is authenticated before making API calls
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');
                const userId = cookie.get('userId');

                // Only proceed if we have both authentication tokens
                if (!xsrfToken || !userId) {
                    console.log('User not authenticated - skipping users fetch');
                    return;
                }

                dispatch({ type: ACTIONS.FETCH_USERS_START });

                const response = await axios.get(
                    `${API_URL}/project-membership/get-users-by-project-id/${projectId}`,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                dispatch({
                    type: ACTIONS.FETCH_USERS_SUCCESS,
                    payload: response.data,
                });
            } catch (error) {
                console.error('Error fetching users:', error);

                let errorMessage = 'Failed to load users';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to view users. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.FETCH_USERS_ERROR,
                    payload: errorMessage,
                });
            }
        };

        if (projectId) {
            fetchUsers();
        }
    }, [projectId]);

    // Set current user
    useEffect(() => {
        const fetchCurentUser = async () => {
            const userId = cookie.get('userId');
            const API_URL = process.env.REACT_APP_API_URL;
            const xsrfToken = cookie.get('XSRF-TOKEN');

            // Only proceed if user is authenticated
            if (!userId || !xsrfToken) {
                console.log('User not authenticated - skipping user role fetch');
                return;
            }

            try {
                const response = await axios.get(
                    `${API_URL}/project-membership/getRoleByProjectId/${projectId}`,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                if (response.data) {
                    dispatch({
                        type: ACTIONS.SET_CURRENT_USER,
                        payload: {
                            role: response.data,
                            userId: userId,
                        },
                    });
                }
            } catch (error) {
                console.error('Error fetching user role:', error);
            }
        };

        if (projectId) {
            fetchCurentUser();
        }
    }, [projectId]);

    // Create computed value for filtered deficiencies
    const filteredDeficiencies = useMemo(() => {
        return state.deficiencies
            .filter((deficiency) => {
                // Hide completed deficiencies unless showCompleted is on
                // or the user explicitly selected "Completed" in the status filter
                if (
                    !state.showCompleted &&
                    !state.statusFilters.includes('Completed') &&
                    deficiency.status === 'Completed'
                ) {
                    return false;
                }
                // Hide rejected deficiencies unless showRejected is on
                // or the user explicitly selected "Rejected" in the status filter
                if (
                    !state.showRejected &&
                    !state.statusFilters.includes('Rejected') &&
                    deficiency.status === 'Rejected'
                ) {
                    return false;
                }
                return true;
            })
            .filter((deficiency) => {
                // Apply internal-only filter
                if (state.internalOnlyFilter) {
                    return deficiency.internalOnly === true;
                }
                return true;
            })
            .filter((deficiency) => {
                // First apply the search filter
                if (state.searchQuery) {
                    const query = state.searchQuery.toLowerCase();
                    const matchTitle = deficiency.title?.toLowerCase().includes(query);
                    const matchDescription = deficiency.description?.toLowerCase().includes(query);
                    const matchLocation = deficiency.location?.toLowerCase().includes(query);
                    const matchAssignee = deficiency.assignedToBusinessName
                        ?.toLowerCase()
                        .includes(query);
                    const matchStatus = deficiency.status?.toLowerCase().includes(query);

                    return (
                        matchTitle ||
                        matchDescription ||
                        matchLocation ||
                        matchAssignee ||
                        matchStatus
                    );
                }

                // If no search query, include all deficiencies
                return true;
            })
            .filter((deficiency) => {
                // Apply status filters (multi-select, OR logic)
                if (state.statusFilters.length > 0) {
                    const role = state.currentUser?.role;
                    if (role) {
                        const displayLabel = getDisplayLabelForInternal(deficiency.status, role);
                        return (
                            state.statusFilters.includes(deficiency.status) ||
                            state.statusFilters.includes(displayLabel)
                        );
                    }
                    return state.statusFilters.includes(deficiency.status);
                }
                return true;
            })
            .filter((deficiency) => {
                // Apply company filters (multi-select, OR logic)
                if (state.companyFilters.length > 0) {
                    return state.companyFilters.some(
                        (c) => c.companyId === deficiency.assignedToBusinessId
                    );
                }
                return true;
            })
            .filter((deficiency) => {
                // Apply location filters (multi-select, OR logic)
                // Supports floorplan-only entries (roomName === null) to match all rooms on a floorplan
                if (state.locationFilters.length > 0) {
                    const room = deficiency.roiName || 'Unassigned Room';
                    const floorplan = deficiency.floorplanName || 'Unknown Floor Plan';
                    return state.locationFilters.some(
                        (l) =>
                            l.floorplanName === floorplan &&
                            (l.roomName === null || l.roomName === room)
                    );
                }
                return true;
            });
    }, [
        state.deficiencies,
        state.searchQuery,
        state.showCompleted,
        state.showRejected,
        state.internalOnlyFilter,
        state.statusFilters,
        state.currentUser?.role,
        state.companyFilters,
        state.locationFilters,
    ]);

    // Update a deficiency, always preserving all fields
    const updateDeficiency = useCallback(
        async (deficiencyId, deficiencyData) => {
            try {
                dispatch({ type: ACTIONS.UPDATE_DEFICIENCY_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                // Always get the current full deficiency from state first
                const currentDeficiency = state.deficiencies.find(
                    (d) => d.deficiencyId === deficiencyId
                );

                if (!currentDeficiency) {
                    throw new Error(`Deficiency with ID ${deficiencyId} not found in state`);
                }

                // Use the sanitize function to ensure proper date preservation
                const completeDeficiencyData = sanitizeForUpdate(currentDeficiency, deficiencyData);

                // Convert status values to API format if present
                if (completeDeficiencyData.status) {
                    completeDeficiencyData.status = getApiStatusValue(
                        completeDeficiencyData.status
                    );
                }

                const response = await axios.put(
                    `${API_URL}/deficiencies/update/${deficiencyId}`,
                    completeDeficiencyData,
                    {
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                // Convert API status back to frontend display value if present
                const responseData = response.data;
                if (responseData && responseData.status) {
                    responseData.status = getDisplayStatusValue(responseData.status);
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_SUCCESS,
                    payload: responseData,
                });

                return responseData;
            } catch (error) {
                console.error('Error updating deficiency:', error);

                let errorMessage = 'Failed to update deficiency';
                if (error.response?.status === 403) {
                    errorMessage = 'You are not authorized to update this deficiency.';
                } else if (error.response?.status === 409) {
                    errorMessage = 'This deficiency was updated by another user. Please refresh.';
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                    payload: errorMessage,
                });

                throw error;
            }
        },
        [dispatch, state.deficiencies]
    );

    // Terminal rejection - sets deficiency to REJECTED status from any status.
    // Used for dropdown override and quick reject from SUBMITTED.
    // Does NOT create a new attempt.
    const rejectTerminal = useCallback(
        async (deficiencyId, rejectionReason) => {
            try {
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                const response = await axios.post(
                    `${API_URL}/deficiencies/${deficiencyId}/reject-terminal`,
                    { rejectionReason },
                    {
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                            'Content-Type': 'application/json',
                        },
                        withCredentials: true,
                    }
                );

                // Convert API status back to frontend display value
                const responseData = response.data;
                if (responseData && responseData.status) {
                    responseData.status = getDisplayStatusValue(responseData.status);
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_SUCCESS,
                    payload: responseData,
                });

                await fetchDeficiencyComments(deficiencyId);
                return response.data;
            } catch (error) {
                console.error('Error rejecting deficiency (terminal):', error);

                let errorMessage = 'Failed to reject deficiency';
                if (error.response?.status === 403) {
                    errorMessage = 'You are not authorized to reject this deficiency.';
                } else if (error.response?.status === 400) {
                    errorMessage = 'Cannot reject deficiency in its current state.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                    payload: errorMessage,
                });

                throw new Error(errorMessage);
            }
        },
        [dispatch, fetchDeficiencyComments]
    );

    // Work rejection - bounces work back to ASSIGNED with a new attempt.
    // Only allowed from WORK_COMPLETED or INTERNALLY_SIGNED_OFF.
    // Marks current attempt as REJECTED (preserving revision history).
    const rejectWork = useCallback(
        async (deficiencyId, rejectionReason, attemptId) => {
            try {
                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                const response = await axios.post(
                    `${API_URL}/deficiencies/${deficiencyId}/reject-work`,
                    { rejectionReason, attemptId },
                    {
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                            'Content-Type': 'application/json',
                        },
                        withCredentials: true,
                    }
                );

                // Convert API status back to frontend display value
                const responseData = response.data;
                if (responseData && responseData.status) {
                    responseData.status = getDisplayStatusValue(responseData.status);
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_SUCCESS,
                    payload: responseData,
                });

                await fetchDeficiencyComments(deficiencyId);
                return response.data;
            } catch (error) {
                console.error('Error rejecting work:', error);

                let errorMessage = 'Failed to reject work';
                if (error.response?.status === 403) {
                    errorMessage = 'You are not authorized to reject this work.';
                } else if (error.response?.status === 409) {
                    errorMessage = 'This deficiency was updated by another user. Please refresh.';
                } else if (error.response?.status === 400) {
                    errorMessage = 'Cannot reject work in its current state.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                    payload: errorMessage,
                });

                throw new Error(errorMessage);
            }
        },
        [dispatch, fetchDeficiencyComments]
    );

    //function to update deficiency images
    const updateDeficiencyImages = useCallback(
        async (deficiencyId, beforeImages, afterImages, preserveEdits = null) => {
            try {
                dispatch({ type: ACTIONS.UPDATE_IMAGES_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.UPDATE_IMAGES_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                // Create FormData for multipart/form-data request
                const formData = new FormData();

                // Add before images if they exist
                if (beforeImages && beforeImages.length > 0) {
                    beforeImages.forEach((image) => {
                        formData.append('beforeImages', image);
                    });
                }

                // Add after images if they exist
                if (afterImages && afterImages.length > 0) {
                    afterImages.forEach((image) => {
                        formData.append('afterImages', image);
                    });
                }

                // Use the correct endpoint for image upload
                const response = await axios.post(
                    `${API_URL}/images/deficiency/${deficiencyId}/upload`,
                    formData,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                            // Content-Type will be set automatically to multipart/form-data
                        },
                    }
                );

                // Get the current deficiency state
                const currentFromState = state.deficiencies.find(
                    (d) => d.deficiencyId === deficiencyId
                );

                // Use the data from the server as the source of truth for images
                // But merge in other fields from our state to maintain context
                const updatedDeficiency = {
                    ...(currentFromState || {}),
                    ...response.data,
                };

                // Apply mapping to status from API response
                updatedDeficiency.status = getDisplayStatusValue(updatedDeficiency.status);

                // If we have local edits to preserve, merge them BUT exclude images
                // because we just got the new ones from the server
                if (preserveEdits) {
                    const { beforeImages, afterImages, ...otherFields } = preserveEdits;
                    Object.assign(updatedDeficiency, otherFields);
                }

                // Update the deficiency in our state
                dispatch({
                    type: ACTIONS.UPDATE_IMAGES_SUCCESS,
                    payload: updatedDeficiency,
                });

                // Return the full updated object for local state sync
                return updatedDeficiency;
            } catch (error) {
                console.error('Error uploading deficiency images:', error);

                let errorMessage = 'Failed to upload deficiency images';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to upload images. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.UPDATE_IMAGES_ERROR,
                    payload: errorMessage,
                });

                throw error;
            }
        },
        [dispatch, state.deficiencies]
    );

    // New function to handle batch updates of deficiencies
    const batchUpdateDeficiencies = useCallback(
        async (deficiencyIds, updateData) => {
            try {
                dispatch({ type: ACTIONS.UPDATE_DEFICIENCY_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                // Keep the frontend status for sanitizeForUpdate which uses frontend status values
                const frontendUpdateData = { ...updateData };

                // Create an array of complete deficiency objects with updated fields
                const deficiencyDTOs = deficiencyIds
                    .map((deficiencyId) => {
                        // Get the current full deficiency from state
                        const currentDeficiency = state.deficiencies.find(
                            (d) => d.deficiencyId === deficiencyId
                        );

                        if (!currentDeficiency) {
                            console.warn(
                                `Deficiency with ID ${deficiencyId} not found in state, skipping`
                            );
                            return null;
                        }

                        // First sanitize with frontend status values to properly handle regression
                        const sanitizedData = sanitizeForUpdate(
                            currentDeficiency,
                            frontendUpdateData
                        );

                        // Then convert status to API format for backend
                        if (sanitizedData.status) {
                            sanitizedData.status = getApiStatusValue(sanitizedData.status);
                        }

                        return sanitizedData;
                    })
                    .filter(Boolean);

                if (deficiencyDTOs.length === 0) {
                    throw new Error('No valid deficiencies found to update');
                }

                // Send the array of complete objects to the batch update API
                const response = await axios.put(
                    `${API_URL}/deficiencies/batch-update-deficiencies`,
                    deficiencyDTOs,
                    {
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'application/json',
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                // Convert API status values back to frontend display values
                const responseData = response.data.map((deficiency) => {
                    if (deficiency && deficiency.status) {
                        return {
                            ...deficiency,
                            status: getDisplayStatusValue(deficiency.status),
                        };
                    }
                    return deficiency;
                });

                // Dispatch success with the updated deficiencies
                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_SUCCESS,
                    payload: responseData,
                });

                // Update the deficiencies in the state
                dispatch({
                    type: ACTIONS.FETCH_DEFICIENCIES_SUCCESS,
                    payload: state.deficiencies.map((def) => {
                        // If this deficiency was updated, replace it with the updated version
                        const updatedDef = responseData.find(
                            (updated) => updated.deficiencyId === def.deficiencyId
                        );
                        return updatedDef || def;
                    }),
                });

                return responseData;
            } catch (error) {
                console.error('Error in batch update of deficiencies:', error);

                let errorMessage = 'Failed to update deficiencies';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to update deficiencies. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.UPDATE_DEFICIENCY_ERROR,
                    payload: errorMessage,
                });

                throw error;
            }
        },
        [dispatch, state.deficiencies]
    );

    // Helper function to get the full URL for an image
    const getImageUrl = useCallback((imageKey) => {
        if (!imageKey) return null;

        const API_URL = process.env.REACT_APP_API_URL;

        // If the imageKey is already a full URL, return it
        if (typeof imageKey === 'string' && imageKey.startsWith('http')) {
            return imageKey;
        }

        // For debugging - logs the raw image key

        return `${API_URL}/images/${imageKey}`;
    }, []);

    //function to delete a deficiency image
    const deleteDeficiencyImage = useCallback(
        async (imageId, deficiencyId, imageType) => {
            try {
                dispatch({ type: ACTIONS.DELETE_IMAGE_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.DELETE_IMAGE_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                // Call the delete image API endpoint
                await axios.delete(`${API_URL}/images/deficiency/deleteImage/${imageId}`, {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                });

                // Update the state after successful deletion
                dispatch({
                    type: ACTIONS.DELETE_IMAGE_SUCCESS,
                    payload: {
                        deficiencyId,
                        imageId,
                        imageType: imageType.toLowerCase(),
                    },
                });

                return true;
            } catch (error) {
                console.error('Error deleting deficiency image:', error);

                let errorMessage = 'Failed to delete image';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to delete images. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.DELETE_IMAGE_ERROR,
                    payload: errorMessage,
                });

                throw error;
            }
        },
        [dispatch]
    );

    // Function to add initial images to a newly created deficiency
    const addInitialImages = useCallback(
        async (deficiencyId, beforeImages) => {
            try {
                dispatch({ type: ACTIONS.ADD_INITIAL_IMAGES_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.ADD_INITIAL_IMAGES_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                // Create FormData for multipart/form-data request
                const formData = new FormData();

                // Add before images
                if (beforeImages && beforeImages.length > 0) {
                    beforeImages.forEach((image) => {
                        formData.append('beforeImages', image);
                    });
                }

                // Use the same endpoint for image upload (API doesn't need to change)
                const response = await axios.post(
                    `${API_URL}/images/deficiency/${deficiencyId}/upload`,
                    formData,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                            // Content-Type will be set automatically to multipart/form-data
                        },
                    }
                );

                // Create payload with only the necessary data
                const updatedDeficiency = {
                    deficiencyId,
                    beforeImages: response.data.beforeImages,
                };

                // Update state with the simplified payload
                dispatch({
                    type: ACTIONS.ADD_INITIAL_IMAGES_SUCCESS,
                    payload: updatedDeficiency,
                });

                return response.data;
            } catch (error) {
                console.error('Error uploading initial deficiency images:', error);

                let errorMessage = 'Failed to upload initial deficiency images';
                if (error.response?.status === 403) {
                    errorMessage = 'Not authorized to upload images. Please log in again.';
                }

                dispatch({
                    type: ACTIONS.ADD_INITIAL_IMAGES_ERROR,
                    payload: errorMessage,
                });

                throw error;
            }
        },
        [dispatch]
    );

    //function to create a deficiency
    const createDeficiency = useCallback(
        async (deficiencyData) => {
            try {
                dispatch({ type: ACTIONS.CREATE_DEFICIENCY_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                //todo: I dont think this is the ideal way to check if user is logged in
                //todo: I dont event think this does anything
                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.CREATE_DEFICIENCY_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return; // Add return to prevent further execution
                }

                // Validate required fields for new API structure
                if (!deficiencyData.floorplanId) {
                    dispatch({
                        type: ACTIONS.CREATE_DEFICIENCY_ERROR,
                        payload: 'floorplanId is required for creating deficiencies.',
                    });
                    return;
                }

                // we wont have afterImages for a new deficiency
                const { beforeImages, ...deficiencyDataDTO } = deficiencyData;

                const deficiencyDataWithProject = {
                    ...deficiencyDataDTO,
                    projectId: projectId,
                    // Ensure floorplanId is included
                    floorplanId: deficiencyData.floorplanId,
                    // roiId is optional - only include if provided
                    ...(deficiencyData.roiId && { roiId: deficiencyData.roiId }),
                };

                const response = await axios.post(
                    `${API_URL}/deficiencies/create`,
                    deficiencyDataWithProject,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                // Get initial deficiency data from response
                const initialDeficiency = response.data;

                // Ensure assignedOn is present immediately if we created in Assigned state
                if (
                    deficiencyDataWithProject.assignedToBusinessId &&
                    !initialDeficiency.assignedOn
                ) {
                    initialDeficiency.assignedOn = getTodayFormatted();
                }

                // Convert API status values back to frontend display values
                initialDeficiency.status = getDisplayStatusValue(initialDeficiency.status);

                // Save the initial deficiency state
                dispatch({ type: ACTIONS.SAVE_NEW_DEFICIENCY, payload: initialDeficiency });

                // Upload images if they exist using the dedicated function
                let finalDeficiency = initialDeficiency;
                if (beforeImages && beforeImages.length > 0) {
                    const response = await addInitialImages(
                        initialDeficiency.deficiencyId,
                        beforeImages
                    );

                    // Use the complete response directly
                    finalDeficiency = response;

                    // Convert API status values back to frontend display values
                    finalDeficiency.status = getDisplayStatusValue(finalDeficiency.status);

                    // Fallback ensure assignedOn exists for immediate UI if assigned
                    if (
                        deficiencyDataWithProject.assignedToBusinessId &&
                        !finalDeficiency.assignedOn
                    ) {
                        finalDeficiency.assignedOn = getTodayFormatted();
                    }
                }

                // Update the state with the final deficiency data
                dispatch({ type: ACTIONS.CREATE_DEFICIENCY_SUCCESS, payload: finalDeficiency });

                return finalDeficiency;
            } catch (error) {
                console.error('Error creating deficiency:', error);
                dispatch({
                    type: ACTIONS.CREATE_DEFICIENCY_ERROR,
                    payload: error.message || 'Error creating deficiency',
                });
                throw error;
            }
        },
        [dispatch, projectId, addInitialImages]
    );

    // Add a comment to a deficiency
    const addDeficiencyComment = useCallback(
        async (deficiencyId, text, files = []) => {
            try {
                dispatch({ type: ACTIONS.ADD_COMMENT_START });

                const API_URL = process.env.REACT_APP_API_URL;
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    dispatch({
                        type: ACTIONS.ADD_COMMENT_ERROR,
                        payload: 'Authentication required. Please log in first.',
                    });
                    return;
                }

                const formData = new FormData();
                formData.append('text', text);
                files.forEach((file) => {
                    formData.append('files', file);
                });

                const response = await axios.post(
                    `${API_URL}/deficiencies/${deficiencyId}/comments`,
                    formData,
                    {
                        withCredentials: true,
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }
                );

                dispatch({
                    type: ACTIONS.ADD_COMMENT_SUCCESS,
                    payload: {
                        deficiencyId,
                        comment: response.data,
                    },
                });

                return response.data;
            } catch (error) {
                console.error('Error adding comment:', error);
                dispatch({
                    type: ACTIONS.ADD_COMMENT_ERROR,
                    payload: error.message || 'Error adding comment',
                });
                throw error;
            }
        },
        [dispatch]
    );
    // Helper to set the current user info
    const setCurrentUser = useCallback(
        (userInfo) => {
            dispatch({
                type: ACTIONS.SET_CURRENT_USER,
                payload: userInfo,
            });
        },
        [dispatch]
    );

    // Transition helper function
    const transitionDeficiency = useCallback(
        (deficiencyId, actionType, additionalData = {}) => {
            // Use the existing updateDeficiency function instead of dispatching a direct action
            // This ensures all updates use the same API endpoint and code path

            // Get the transition configuration to determine the target status
            const transition = STATE_TRANSITIONS[actionType];
            if (!transition) {
                console.error(`No transition configuration found for action type: ${actionType}`);
                return Promise.reject(new Error(`Invalid transition action: ${actionType}`));
            }

            // Get the deficiency to transition
            const deficiency = state.deficiencies.find((d) => d.deficiencyId === deficiencyId);
            if (!deficiency) {
                console.error(`Deficiency not found: ${deficiencyId}`);
                return Promise.reject(new Error(`Deficiency not found: ${deficiencyId}`));
            }

            // Check if the transition is allowed based on role and current status
            if (
                !canTransition(
                    deficiency.status,
                    actionType,
                    state.currentUser.role,
                    deficiency.internallyCreated
                )
            ) {
                const errorMsg = `Transition ${actionType} not allowed from status ${deficiency.status} for role ${state.currentUser.role}`;
                console.warn(errorMsg);
                return Promise.reject(new Error(errorMsg));
            }

            // Create update data with the new status and any additional provided data
            const updateData = {
                status: transition.to,
                ...additionalData,
            };

            // Use the existing updateDeficiency function to save the changes
            return updateDeficiency(deficiencyId, updateData);
        },
        [state.deficiencies, state.currentUser.role, updateDeficiency]
    );

    // Create a memoized value for the context
    const contextValue = useMemo(() => {
        return {
            state,
            dispatch,
            // Include computed values
            filteredDeficiencies,
            updateDeficiency,
            updateDeficiencyImages,
            deleteDeficiencyImage,
            batchUpdateDeficiencies,
            createDeficiency,
            loadFloorplanDeficiencies,
            getImageUrl, // Add the new helper function to context
            fetchDeficiencyComments,
            addDeficiencyComment,
            rejectTerminal,
            rejectWork,
            // Add transition helper functions
            transitionDeficiency,
            // Helper to set the current user info
            setCurrentUser,
        };
    }, [
        state,
        filteredDeficiencies,
        updateDeficiency,
        updateDeficiencyImages,
        deleteDeficiencyImage,
        batchUpdateDeficiencies,
        createDeficiency,
        loadFloorplanDeficiencies,
        fetchDeficiencyComments,
        addDeficiencyComment,
        rejectTerminal,
        rejectWork,
        transitionDeficiency,
        setCurrentUser,
        getImageUrl,
    ]);

    return <DeficiencyContext.Provider value={contextValue}>{children}</DeficiencyContext.Provider>;
}

/**
 * Custom hook for accessing the deficiency context
 * Provides access to state, dispatch, and computed values from context
 *
 * @returns {Object} Context value containing state, dispatch and filteredDeficiencies
 * @throws {Error} If used outside of a DeficiencyProvider
 */
export function useDeficiencies() {
    const context = useContext(DeficiencyContext);
    if (context === undefined) {
        throw new Error('useDeficiencies must be used within a DeficiencyProvider');
    }
    return context;
}
